import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { colors } from "../../theme/designSystem";
import {
  patientTimelineService,
  TreatmentOutcomeType,
} from "../../services/patientTimelineService";

interface SpecialtyOption {
  id: string;
  name: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  patientId: string;
  entityId: string;
  specialties: SpecialtyOption[];
}

const today = (): string => new Date().toISOString().slice(0, 10);

// Valor sentinela do combo de especialidade: libera o campo de texto livre (RN-526e).
const OTHER_SPECIALTY = "__other__";

const TreatmentOutcomeModal: React.FC<Props> = ({
  isOpen, onClose, onSaved, patientId, entityId, specialties,
}) => {
  const [types, setTypes] = useState<TreatmentOutcomeType[]>([]);
  const [outcomeTypeId, setOutcomeTypeId] = useState("");
  const [outcomeDate, setOutcomeDate] = useState(today());
  const [specialtyId, setSpecialtyId] = useState("");
  const [specialtyOther, setSpecialtyOther] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setError("");
    setOutcomeDate(today());
    setSpecialtyId("");
    setSpecialtyOther("");
    setNotes("");
    patientTimelineService
      .outcomeTypes()
      .then((t) => {
        setTypes(t);
        // Sem pré-seleção: registrar desfecho é decisão clínica, não pode sair por inércia
        // de um valor que já veio marcado.
        setOutcomeTypeId("");
      })
      .catch(() => setError("Não foi possível carregar os tipos de desfecho."));
  }, [isOpen]);

  const selectedType = types.find((t) => t.id === outcomeTypeId);
  // Marco que não encerra ("Outro", "Ocorrência") precisa dizer o que aconteceu: sem texto o
  // registro não significa nada para quem ler a linha do tempo depois.
  const notesRequired = !!selectedType && !selectedType.endsTreatment;
  const otherSpecialty = specialtyId === OTHER_SPECIALTY;

  const handleSave = async () => {
    if (!outcomeTypeId || !outcomeDate) {
      setError("Informe o tipo e a data do marco.");
      return;
    }

    if (notesRequired && !notes.trim()) {
      setError("Descreva o que aconteceu — este tipo de registro exige a descrição.");
      return;
    }

    if (otherSpecialty && !specialtyOther.trim()) {
      setError("Informe qual é a especialidade.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      await patientTimelineService.createOutcome(patientId, {
        entityId,
        outcomeTypeId,
        outcomeDate,
        // Vazio = desfecho geral (saída da clínica), não uma especialidade específica.
        professionalTypeId: otherSpecialty ? null : specialtyId || null,
        specialtyOther: otherSpecialty ? specialtyOther.trim() : null,
        notes: notes.trim() || null,
      });
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.description || "Não foi possível registrar o marco.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px", boxShadow: "0 10px 30px rgba(0,0,0,0.3)" } }}
    >
      <DialogTitle
        sx={{
          backgroundColor: colors.primary, color: colors.white, padding: "1.5rem",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}
      >
        <Typography variant="h6" component="h3" sx={{ fontSize: "1.25rem", fontWeight: 600 }}>
          Registrar marco
        </Typography>
        <IconButton onClick={onClose} sx={{ color: colors.white, padding: "0.25rem" }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField
            select
            label="Tipo"
            value={outcomeTypeId}
            onChange={(e) => setOutcomeTypeId(e.target.value)}
            helperText={selectedType?.description || "Escolha o tipo para ver quando ele se aplica"}
            size="small"
            InputLabelProps={{ shrink: true }}
            FormHelperTextProps={{ sx: { lineHeight: 1.45, mt: 0.75 } }}
            fullWidth
          >
            <MenuItem value="">
              <em>Selecione</em>
            </MenuItem>
            {types.map((t) => (
              <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Especialidade"
            value={specialtyId}
            onChange={(e) => setSpecialtyId(e.target.value)}
            helperText="Deixe em branco para registrar saída da clínica como um todo"
            size="small"
            InputLabelProps={{ shrink: true }}
            fullWidth
          >
            <MenuItem value="">Todas — saída da clínica</MenuItem>
            {specialties.map((s) => (
              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
            ))}
            <MenuItem value={OTHER_SPECIALTY}>Outra — descrever</MenuItem>
          </TextField>

          {otherSpecialty && (
            <TextField
              label="Qual especialidade"
              value={specialtyOther}
              onChange={(e) => setSpecialtyOther(e.target.value)}
              placeholder="Ex.: Musicoterapia, Equoterapia"
              helperText="Fica só neste registro — não entra no catálogo da clínica"
              size="small"
              required
              inputProps={{ maxLength: 100 }}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          )}

          <TextField
            label="Data do marco"
            type="date"
            value={outcomeDate}
            onChange={(e) => setOutcomeDate(e.target.value)}
            helperText="A data em que o desfecho aconteceu, não a de hoje"
            size="small"
            InputLabelProps={{ shrink: true }}
            fullWidth
          />

          <TextField
            label={notesRequired ? "Descrição" : "Observação"}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="O que motivou este marco (aparece na linha do tempo)"
            helperText={notesRequired
              ? "Obrigatória: este marco não encerra o tratamento, é a descrição que lhe dá sentido."
              : undefined}
            required={notesRequired}
            multiline
            minRows={3}
            size="small"
            inputProps={{ maxLength: 1000 }}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />

          {error && (
            <Box sx={{ color: colors.error, fontSize: "0.85rem" }}>{error}</Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", color: colors.textSecondary }}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="contained"
          sx={{
            textTransform: "none",
            backgroundColor: colors.primary,
            "&:hover": { backgroundColor: colors.primaryHover },
          }}
        >
          {saving ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Registrar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TreatmentOutcomeModal;
